// For localised fare/filter labels in React components, use:
//   useFlightFareLabels (hooks/Flights/useFlightFareLabels)
//   useFlightSortOptions (hooks/Flights/useFlightSortOptions)
//   useFlightFilterLabels (hooks/Flights/useFlightFilterLabels)
import type { IntlShape } from 'react-intl'

export const FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES = 20

export const maxFlightChildAge = 11
export const maxFlightInfantAge = 1
export const maxFlightPassengerCount = 9

/**
 * Default carry-on baggage weight in kg when not specified in fare details.
 * This is a common industry standard for carry-on baggage allowance.
 */
export const DEFAULT_CARRY_ON_BAGGAGE_KG = 7

// Luxury Escapes marketing airline IATA code
export const AIRLINE_CODE_LE = 'L0'

export const VIRGIN_AUSTRALIA = 'Virgin Australia'

export const PASSENGER_TYPE = {
  ADULT: 'adult',
  CHILD: 'child',
  INFANT: 'infant',
} as const

export const titles: Record<string, Record<string, string>> = {
  adult: {
    mr: 'Mr.',
    ms: 'Ms.',
    mrs: 'Mrs.',
    miss: 'Miss.',
    dr: 'Dr.',
  },
  child: {
    mstr: 'Master',
    miss: 'Miss.',
  },
  infant: {
    mstr: 'Master',
    miss: 'Miss.',
  },
}

export function getFlightTitleLabels(intl: IntlShape): Record<string, Record<string, string>> {
  const mrLabel = intl.formatMessage({
    id: '-IoY-M4EpcvGi81CxYB1',
    defaultMessage: 'Mr',
    description: 'Honorific title option for Mister in a person title select dropdown',
  })
  const msLabel = intl.formatMessage({
    id: 'wMCJpdsywUKu9Gt009MQ',
    defaultMessage: 'Ms',
    description: 'Honorific title option for Ms in a person title select dropdown field',
  })
  const mrsLabel = intl.formatMessage({
    id: 'zc6TetZ6YawzKM3NyzBc',
    defaultMessage: 'Mrs',
    description: 'Honorific title option for Mrs in a person title select dropdown field',
  })
  const missLabel = intl.formatMessage({
    id: 'gE4hCiXpTcNFRivND74x',
    defaultMessage: 'Miss',
    description: 'Honorific title option for Miss in a person title select dropdown field',
  })
  const masterLabel = intl.formatMessage({
    id: 'F00hZeaSMHvMLA-tltdW',
    defaultMessage: 'Master',
    description: 'Honorific title option for Master in a person title select dropdown',
  })
  const drLabel = intl.formatMessage({
    id: 'RMffxRWL04ldU4qkGMZC',
    defaultMessage: 'Dr',
    description: 'Honorific title option for Doctor in a person title select dropdown',
  })

  const withPeriod = (label: string) => `${label}.`

  return {
    adult: {
      mr: withPeriod(mrLabel),
      ms: withPeriod(msLabel),
      mrs: withPeriod(mrsLabel),
      miss: withPeriod(missLabel),
      dr: withPeriod(drLabel),
    },
    child: {
      mstr: masterLabel,
      miss: withPeriod(missLabel),
    },
    infant: {
      mstr: masterLabel,
      miss: withPeriod(missLabel),
    },
  }
}

export const frequentFlyerPrograms = {
  qf: 'Qantas',
  ek: 'Emirates',
  jl: 'Vietnam Airlines (Lotus Miles)',
  vn: 'Japan Airlines (JAL Miles)',
}

export const getPrimaryPassengerState = (): Partial<App.Passenger> => ({
  type: 'adult',
  isPrimary: true,
  addedExtras: {
    baggage: {},
    carryOnBaggage: {},
  },
})

export const getAdultPassengerState = (): Partial<App.Passenger> => ({
  type: 'adult',
  isPrimary: false,
  addedExtras: {
    baggage: {},
    carryOnBaggage: {},
  },
})

export const getChildPassengerState = (): Partial<App.Passenger> => ({
  type: 'child',
  isPrimary: false,
  addedExtras: {
    baggage: {},
    carryOnBaggage: {},
  },
})

export const getInfantPassengerState = (): Partial<App.Passenger> => ({
  type: 'infant',
  isPrimary: false,
  addedExtras: {
    baggage: {},
    carryOnBaggage: {},
  },
})

export enum FlightSortOptions {
  Best = 'best',
  Cheapest = 'cheapest',
  Fastest = 'fastest',
  EarliestTakeOff = 'earliestTakeOff',
  LatestTakeOff = 'atestTakeOff',
  EarliestLanding = 'earliestLanding',
  LatestLanding = 'latestLanding',
}

export const fareClasses = ['economy', 'premium', 'business', 'first', 'lux_row', 'lux_row_pax_one'] as const
export const fareTypesConsts = ['return', 'oneWay', 'multiCity'] as const

export type FareClass = typeof fareClasses[number]
export type FareType = typeof fareTypesConsts[number]

export enum FlightTileType {
  BUNDLING = 'BUNDLING',
  STANDALONE = 'STANDALONE',
  INFO = 'INFO',
  SUMMARY_ITEM = 'SUMMARY_ITEM',
  CHECKOUT = 'CHECKOUT',
}

export enum FlightsFareTypes {
  RETURN = 'return',
  ONE_WAY = 'oneWay',
  MULTI_CITY = 'multiCity',
}

export enum FlightsViewType {
  TWO_ONE_WAYS = 'TwoOneWays',
}

export enum FlightsTripType {
  ONE_WAY = 'OneWay',
}

export enum FlightsClassTypes {
  ECONOMY = 'economy',
  PREMIUM = 'premium',
  LUXROW = 'lux_row',
  LUXROW_PAX_ONE = 'lux_row_pax_one',
  BUSINESS = 'business',
  FIRST = 'first',
}
export const defaultFareClass = FlightsClassTypes.ECONOMY

/**
 * Maldives charter Société reward fare classes — a payment-method overlay (cash + points)
 * on top of the base cabin, *not* a different cabin. Kept separate from {@link FlightsClassTypes}
 * so generic flight code (search, analytics, contracts) only ever sees base cabins.
 */
export enum RewardFareClass {
  ECONOMY_REWARDS = 'economy_rewards',
  PREMIUM_REWARDS = 'premium_rewards',
  BUSINESS_REWARDS = 'business_rewards',
}

/** Union used by Maldives charter surfaces that can show either cash or reward fare classes. */
export type FareClassWithReward = FlightsClassTypes | RewardFareClass

export function isRewardFareClass(fareClass: FareClassWithReward): fareClass is RewardFareClass {
  return fareClass === RewardFareClass.ECONOMY_REWARDS
    || fareClass === RewardFareClass.PREMIUM_REWARDS
    || fareClass === RewardFareClass.BUSINESS_REWARDS
}

export function isValidFareClass(fc: string | undefined): fc is FlightsClassTypes {
  return typeof fc === 'string' &&
    Object.values(FlightsClassTypes).includes(fc as FlightsClassTypes)
}

export function isValidFareClassWithReward(fc: string | undefined): fc is FareClassWithReward {
  return isValidFareClass(fc) || (typeof fc === 'string' && Object.values(RewardFareClass).includes(fc as RewardFareClass))
}

export const fareClassToApiCabinMap: Record<FlightsClassTypes, string> = {
  [FlightsClassTypes.ECONOMY]: 'Economy',
  [FlightsClassTypes.PREMIUM]: 'Premium Economy',
  [FlightsClassTypes.BUSINESS]: 'Business',
  [FlightsClassTypes.FIRST]: 'First',
  [FlightsClassTypes.LUXROW]: 'SkyLounge',
  [FlightsClassTypes.LUXROW_PAX_ONE]: 'SkyLounge',
}

export const labelFareCabinMap = {
  Economy: FlightsClassTypes.ECONOMY,
  Premium: FlightsClassTypes.PREMIUM,
  Business: FlightsClassTypes.BUSINESS,
  First: FlightsClassTypes.FIRST,
  LuxRow: FlightsClassTypes.LUXROW,
}

export const fareClassToUrlMap: Record<FareClassWithReward, string> = {
  [FlightsClassTypes.ECONOMY]: 'economy',
  [FlightsClassTypes.PREMIUM]: 'economy-plus',
  [FlightsClassTypes.BUSINESS]: 'business',
  [FlightsClassTypes.FIRST]: 'first',
  [FlightsClassTypes.LUXROW]: 'skylounge',
  [FlightsClassTypes.LUXROW_PAX_ONE]: 'skylounge',
  [RewardFareClass.ECONOMY_REWARDS]: 'economy-rewards',
  [RewardFareClass.PREMIUM_REWARDS]: 'economy-plus-rewards',
  [RewardFareClass.BUSINESS_REWARDS]: 'business-rewards',
}

export const urlToFareClassMap: Record<string, FareClassWithReward> = {
  'economy': FlightsClassTypes.ECONOMY,
  'economy-plus': FlightsClassTypes.PREMIUM,
  'business': FlightsClassTypes.BUSINESS,
  'first': FlightsClassTypes.FIRST,
  'skylounge': FlightsClassTypes.LUXROW,
  'economy-rewards': RewardFareClass.ECONOMY_REWARDS,
  'economy-plus-rewards': RewardFareClass.PREMIUM_REWARDS,
  'business-rewards': RewardFareClass.BUSINESS_REWARDS,
}

export const validFareClassUrlValues = Object.keys(urlToFareClassMap)

export function parseFareClassFromUrl(urlValue: string | undefined): FareClassWithReward | undefined {
  if (!urlValue) return undefined
  return urlToFareClassMap[urlValue]
}

export function createFareClassUrlParser() {
  const parser = {
    parse: (value: string): FareClassWithReward | null => {
      return urlToFareClassMap[value] ?? null
    },
    serialize: (value: FareClassWithReward): string => {
      return fareClassToUrlMap[value]
    },
    withDefault: (defaultValue: FareClassWithReward) => ({
      ...parser,
      defaultValue,
    }),
  }
  return parser
}

export const fareClassUrlParser = createFareClassUrlParser()

export function getChildrenSelectWarning(intl: IntlShape): string {
  return intl.formatMessage({
    id: 'SHFYUIUmwuBnf9jqzs28',
    defaultMessage: 'Children under the age of 2 will be assigned an infant’s flight ticket and must sit on an accompanying adult’s lap.',
    description: 'Warning shown when selecting children under 2 for flight booking',
  })
}

export function getMaxInfantsWarning(intl: IntlShape): string {
  return intl.formatMessage({
    id: 'jM1DMW18Fp7Ex7WiM3AC',
    defaultMessage: 'Cannot have more infants than adults - please call us for assistance with your booking',
    description: 'Warning shown when number of infants exceeds number of adults',
  })
}

export const flightsImageId = 'Flights_wl4url'

export const validFlightDateRangeInMonths = 11

export const defaultAirlineLogo = 'kw12fpjilpkaamli2ufq'

// IATA Carrier codes are guranteed to be the first 2 alphanumerics
export const FLIGHT_CODE_REGEX = /^[a-zA-Z0-9]{2}\s?\d{1,4}$/

export const DEFAULT_ADULT_OCCUPANTS = 1

export enum FlightViewTypes {
  TWO_ONE_WAYS = 'TwoOneWays',
  RETURN = 'Return',
  ONE_WAY = 'OneWay',
  TWO_ONE_WAYS_AND_RETURN = 'TwoOneWaysAndReturn',
  MULTI_CITY = 'MultiCity',
}

/**
 * This is the name of the fields that are users interact with on the search page
 * We use this list of filters that change the shown flights in place (without needing to search again)
 * 'search' filters (such as date/airports etc) are not included here as they modify the entire search
 */
export const FlightsSearchInPlaceFilters = [
  'airlines',
  'stops',
  'stopDurations',
  'cabinTypes',
  'durations',
  'minDepartureTime',
  'maxDepartureTime',
  'minArrivalTime',
  'maxArrivalTime',
  'recommended',
  'bags',
  'stopOverCities',
  'alliances',
]

export enum FlightFilterType {
  AIRLINES = 'airlines',
  CABIN_TYPES = 'cabinTypes',
  STOP_DURATIONS = 'stopDurations',
  STOPS = 'stops',
  DURATIONS = 'durations',
  PRICE_FILTER_TYPE = 'priceFilterType',
  MIN_PRICES = 'minPrices',
  MAX_PRICES = 'maxPrices',
  MIN_DEPARTURE_TIME = 'minDepartureTime',
  MAX_DEPARTURE_TIME = 'maxDepartureTime',
  MIN_ARRIVAL_TIME = 'minArrivalTime',
  MAX_ARRIVAL_TIME = 'maxArrivalTime',
  PRICES = 'prices',
  TIMES = 'times',
  DEPARTURE_TIMES = 'departureTimes',
  ARRIVAL_TIMES = 'arrivalTimes',
  RECOMMENDED = 'recommended',
  BAGS = 'bags',
  STOP_OVER_CITIES = 'stopOverCities',
  ALLIANCES = 'alliances',
  APPLIED_FILTERS = 'applied_filters',
}

export enum FlightSegment {
  DEPARTING = 'departing',
  RETURNING = 'returning',
}

export const STANDALONE_FLIGHT_FILTERS = [
  FlightFilterType.AIRLINES,
  FlightFilterType.CABIN_TYPES,
  FlightFilterType.DURATIONS,
  FlightFilterType.PRICES,
  FlightFilterType.STOPS,
  FlightFilterType.STOP_DURATIONS,
  FlightFilterType.TIMES,
  FlightFilterType.RECOMMENDED,
  FlightFilterType.BAGS,
  FlightFilterType.STOP_OVER_CITIES,
  FlightFilterType.ALLIANCES,
]

export const CHECKOUT_FLIGHT_FILTERS = [
  FlightFilterType.AIRLINES,
  FlightFilterType.CABIN_TYPES,
  FlightFilterType.DURATIONS,
  FlightFilterType.STOPS,
  FlightFilterType.STOP_DURATIONS,
  FlightFilterType.TIMES,
  FlightFilterType.RECOMMENDED,
  FlightFilterType.BAGS,
  FlightFilterType.STOP_OVER_CITIES,
  FlightFilterType.ALLIANCES,
]

export const FLEXIBILITY_INCLUSIONS = new Set<App.FareFamilyFeatureType>(['cancellation', 'changes', 'refunds'])

export const FARE_FEATURE_STATUS = {
  INCLUDED: 'INCLUDED',
  NOT_OFFERED: 'NOT_OFFERED',
  CHARGED: 'CHARGED',
} as const

export const FARE_FEATURE_TYPE = {
  CHECKED_BAGGAGE: 'checked_baggage',
} as const

export const TRAVEL_FUSION = 'travelFusion'

export const MAX_MOBILE_CAROUSEL_ITEM_COUNT = 8

export const DISABLED_MULTI_CITY_REGION_CODES = ['IN']

export const JETSTAR_PORTAL = 'https://booking.jetstar.com/mmb'

export const FLIGHTS_MIN_LEAD_TIME_DAYS = 1
export const FLIGHTS_MAX_LEAD_TIME_DAYS = 330

export const FLIGHT_DEAL_PAGE_PATH = '/:region/flight/deal'
